/**
 * KPI Types
 *
 * TypeScript interfaces for the KPI tracking feature.
 * Uses snake_case internally following frontend conventions.
 */

// =============================================================================
// Enums and Constants
// =============================================================================

export type KpiFrequency = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
export type KpiCategory = 'cost' | 'schedule' | 'quality' | 'experience' | 'safety';
export type KpiPeriodType = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
export type OrientationRule = 'gte_goal' | 'lte_goal' | 'eq_goal' | 'inside_range' | 'outside_range';
export type NumberFormat = 'auto' | 'number' | 'integer' | 'decimal_1' | 'decimal_2';

export const NUMBER_FORMATS: { value: NumberFormat; label: string; example: string }[] = [
  { value: 'auto', label: 'Auto (K/M)', example: '1.5K, 2.3M' },
  { value: 'number', label: 'Full Number', example: '1,234,567' },
  { value: 'integer', label: 'Integer', example: '1,235' },
  { value: 'decimal_1', label: '1 Decimal', example: '1,234.6' },
  { value: 'decimal_2', label: '2 Decimals', example: '1,234.57' },
];

export const KPI_FREQUENCIES: { value: KpiFrequency; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

export const KPI_CATEGORIES: { value: KpiCategory; label: string; description: string }[] = [
  { value: 'cost', label: 'Cost', description: 'Financial efficiency, spend per unit, margins' },
  { value: 'schedule', label: 'Schedule', description: 'Timeliness, throughput, capacity, lead times' },
  { value: 'quality', label: 'Quality', description: 'Defects, accuracy, rework, calibration' },
  { value: 'experience', label: 'Experience', description: 'Customer & employee satisfaction, NPS, health' },
  { value: 'safety', label: 'Safety', description: 'Incidents, compliance, wellness (industry-specific)' },
];

export type IndicatorType = 'leading' | 'lagging';

export const INDICATOR_TYPES: { value: IndicatorType; label: string; description: string }[] = [
  { value: 'leading', label: 'Leading', description: 'Predicts future results — activities, inputs, pipeline' },
  { value: 'lagging', label: 'Lagging', description: 'Measures past outcomes — revenue, profit, churn' },
];

export const ORIENTATION_RULES: { value: OrientationRule; label: string; description: string }[] = [
  { value: 'gte_goal', label: 'Higher is Better', description: 'On track when value >= goal' },
  { value: 'lte_goal', label: 'Lower is Better', description: 'On track when value <= goal' },
  { value: 'eq_goal', label: 'Exact Match', description: 'On track when value = goal' },
  { value: 'inside_range', label: 'Within Range', description: 'On track when value is between min and max' },
  { value: 'outside_range', label: 'Outside Range', description: 'On track when value is below min or above max' },
];

// =============================================================================
// KPI Definition Types
// =============================================================================

export interface KpiDefinition {
  id: string;
  name: string;
  description: string | null;
  category: KpiCategory;
  goal_unit: string | null;
  goal_value: number | null;
  goal_min: number | null;
  goal_max: number | null;
  orientation_rule: OrientationRule;
  frequency: KpiFrequency;
  number_format: NumberFormat;
  show_total: boolean;
  show_average: boolean;
  show_goal: boolean;
  owner_id: string | null;
  owner_name: string | null;
  /** Legacy Mongo organisation id — removed in Phase 4 of the account_id migration. */
  organisation_id: string;
  account_id: string | null;
  team_ids: string[];
  indicator_type: IndicatorType;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface KpiDefinitionsResponse {
  definitions: KpiDefinition[];
  page_number: number;
  page_length: number;
  total: number;
}

// =============================================================================
// KPI Value Types
// =============================================================================

export interface KpiValue {
  id: string;
  kpi_id: string;
  /** Legacy Mongo organisation id — removed in Phase 4 of the account_id migration. */
  organisation_id: string;
  account_id: string | null;
  datetime: string;
  value: number;
  notes: string | null;
  is_archived: boolean;
  created_at: string;
  // Joined fields
  kpi_name?: string;
  kpi_category?: KpiCategory;
  kpi_unit?: string | null;
}

export interface KpiValuesResponse {
  values: KpiValue[];
  page_number: number;
  page_length: number;
  total: number;
}

// =============================================================================
// KPI Snapshot Types
// =============================================================================

export interface KpiSnapshot {
  id: string;
  kpi_id: string;
  /** Legacy Mongo organisation id — removed in Phase 4 of the account_id migration. */
  organisation_id: string;
  account_id: string | null;
  period_type: KpiPeriodType;
  period_start: string;
  period_end: string;
  period_label: string | null;
  value_sum: number | null;
  value_avg: number | null;
  value_count: number;
  value_min: number | null;
  value_max: number | null;
  latest_value: number | null;
  latest_value_id: string | null;
  on_track: boolean | null;
  computed_at: string;
  version: number;
}

// =============================================================================
// Scorecard Types
// =============================================================================

/** Value for a single period in the scorecard */
export interface ScorecardPeriodValue {
  period_start: string;
  period_end: string;
  period_label: string;
  value: number | null;
  value_sum: number | null;
  value_avg: number | null;
  value_count: number;
  on_track: boolean | null;
  snapshot_id: string | null;
  notes: string | null;
}

/** KPI data in the scorecard */
export interface ScorecardKpi {
  id: string;
  name: string;
  description: string | null;
  owner_id: string | null;
  owner_name: string | null;
  goal_value: number | null;
  goal_unit: string | null;
  goal_min: number | null;
  goal_max: number | null;
  orientation_rule: OrientationRule;
  number_format: NumberFormat;
  show_total: boolean;
  show_average: boolean;
  show_goal: boolean;
  indicator_type: IndicatorType;
  team_ids: string[];
  color: string | null;
  current_value: number | null;
  average: number | null;
  total: number | null;
  on_track: boolean | null;
  values: ScorecardPeriodValue[];
}

/** Category group in scorecard response */
export interface ScorecardCategory {
  name: KpiCategory;
  kpis: ScorecardKpi[];
}

/** Period info in scorecard response */
export interface ScorecardPeriod {
  label: string;
  start: string;
  end: string;
}

export interface ScorecardResponse {
  categories: ScorecardCategory[];
  periods: ScorecardPeriod[];
  period_type: KpiPeriodType;
  /** Legacy Mongo organisation id — removed in Phase 4 of the account_id migration. */
  organisation_id: string;
  account_id: string | null;
}

// =============================================================================
// Input Types
// =============================================================================

export interface CreateKpiDefinitionInput {
  name: string;
  description?: string;
  category: KpiCategory;
  goal_unit?: string;
  goal_value?: number;
  goal_min?: number;
  goal_max?: number;
  orientation_rule?: OrientationRule;
  frequency?: KpiFrequency;
  number_format?: NumberFormat;
  show_total?: boolean;
  show_average?: boolean;
  show_goal?: boolean;
  owner_id?: string;
  owner_name?: string;
  team_ids?: string[];
  indicator_type: IndicatorType;
}

export interface UpdateKpiDefinitionInput {
  name?: string;
  description?: string | null;
  category?: KpiCategory;
  goal_unit?: string | null;
  goal_value?: number | null;
  goal_min?: number | null;
  goal_max?: number | null;
  orientation_rule?: OrientationRule;
  frequency?: KpiFrequency;
  number_format?: NumberFormat;
  show_total?: boolean;
  show_average?: boolean;
  show_goal?: boolean;
  owner_id?: string | null;
  owner_name?: string | null;
  team_ids?: string[];
  indicator_type?: IndicatorType;
  is_active?: boolean;
}

export interface CreateKpiValueInput {
  kpi_id: string;
  datetime: string;
  value: number;
  notes?: string;
}

export interface CreateKpiValuesBulkInput {
  kpi_id: string;
  values: Array<{
    datetime: string;
    value: number;
    notes?: string;
  }>;
}

export interface UpdateKpiValueInput {
  datetime?: string;
  value?: number;
  notes?: string | null;
}

// =============================================================================
// Filter Types
// =============================================================================

export interface KpiDefinitionFilters {
  category?: KpiCategory;
  frequency?: KpiFrequency;
  is_active?: boolean;
  search?: string;
  page_number?: number;
  page_length?: number;
  sort_key?: string;
  sort_direction?: 1 | -1;
}

export interface KpiValueFilters {
  kpi_id?: string;
  from_date?: string;
  to_date?: string;
  is_archived?: boolean;
  page_number?: number;
  page_length?: number;
  sort_key?: string;
  sort_direction?: 1 | -1;
}

export interface ScorecardFilters {
  category?: KpiCategory;
  period_type?: KpiPeriodType;
  team_id?: string;
  owner_ids?: string[];
}

// =============================================================================
// Completion Stats Types
// =============================================================================

export interface CompletionStatsPeriod {
  start: string;
  end: string;
  type: KpiPeriodType;
  label: string;
}

export interface CompletionStatsOverall {
  total: number;
  completed: number;
  percentage: number;
}

export interface CompletionStatsByTeam {
  team_id: string;
  team_name: string;
  total: number;
  completed: number;
  percentage: number;
}

export interface CompletionStatsByOwner {
  owner_id: string;
  owner_name: string;
  total: number;
  completed: number;
  percentage: number;
}

export interface CompletionStatsResponse {
  period: CompletionStatsPeriod;
  overall: CompletionStatsOverall;
  by_team: CompletionStatsByTeam[];
  by_owner: CompletionStatsByOwner[];
}

export interface CompletionStatsFilters {
  period_type: KpiPeriodType;
  period_start?: string;
  team_id?: string | null;
}
